/**
 * "Segnala una correzione": the visitor tells us what they found.
 *
 * The one screen in this app that writes anywhere. It asks for one thing at a
 * time — the status, the name, or the address — because a form with every
 * field on it is a form people abandon, and because these are three different
 * claims that deserve to be judged separately.
 *
 * Nothing sent here appears on the site by itself. It joins a queue the
 * maintainer reads and applies; what changed is that applying is now instant,
 * because the catalogue is a database rather than a file waiting for a deploy.
 */

import { el, formatDate } from './format.js';

const ENDPOINT = '/api/segnalazioni';

/** The three claims a visitor can make, and how the form asks for each. */
const KINDS = [
  { id: 'stato', label: 'Aperto o chiuso' },
  { id: 'nome', label: 'Ha cambiato nome' },
  { id: 'indirizzo', label: 'Si è spostato' },
];

const MESSAGES = {
  ok: 'Grazie. La controlliamo prima di aggiornare la scheda.',
  rete: 'Non è stato possibile inviare la segnalazione. Controlla la connessione e riprova.',
  generico: 'Non è stato possibile inviare la segnalazione.',
};

/**
 * Today on the visitor's own calendar. `toISOString` is UTC, which in Italy
 * is still yesterday for the first hour or two after midnight: the date field
 * opened on the wrong day and would not let today be picked.
 */
const today = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

function field(labelText, control) {
  const wrapper = el('div', 'field');
  const label = el('label', null, labelText);
  label.htmlFor = control.id;
  wrapper.append(label, control);
  return wrapper;
}

export function createReportView({ container, onClose }) {
  let venue = null;
  let chosen = null;
  let kind = 'stato';

  function render() {
    const form = el('form', 'report__form');
    form.noValidate = true;

    form.append(el('h2', 'report__name', venue.nome ?? venue.id));
    form.append(el('p', 'report__intro', 'Cosa vuoi segnalare?'));

    // --- which kind of correction ---
    const kinds = el('div', 'choice choice--wrap');
    for (const entry of KINDS) {
      const button = el('button', 'btn', entry.label);
      button.type = 'button';
      button.setAttribute('aria-pressed', String(entry.id === kind));
      button.classList.toggle('is-chosen', entry.id === kind);
      button.addEventListener('click', () => {
        if (kind === entry.id) return;
        kind = entry.id;
        chosen = null;
        // Redrawn rather than toggled: each kind asks for different fields,
        // and a half-filled field from the previous one would travel with it.
        container.replaceChildren(render());
        container.scrollTop = 0;
      });
      kinds.append(button);
    }
    form.append(kinds);

    const buttons = new Map();
    const nameInput = el('input');
    const addressInput = el('input');
    const zoneInput = el('input');

    if (kind === 'stato') {
      form.append(el('p', 'report__intro', 'Cosa hai trovato?'));
      const choice = el('div', 'choice');
      for (const value of ['aperto', 'chiuso']) {
        const button = el('button', 'btn', value === 'aperto' ? 'Era aperto' : 'Era chiuso');
        button.type = 'button';
        button.setAttribute('aria-pressed', 'false');
        button.addEventListener('click', () => {
          chosen = value;
          for (const [key, node] of buttons) {
            const on = key === chosen;
            node.classList.toggle('is-chosen', on);
            node.setAttribute('aria-pressed', String(on));
          }
          status.textContent = '';
        });
        buttons.set(value, button);
        choice.append(button);
      }
      form.append(choice);
    }

    if (kind === 'nome') {
      nameInput.type = 'text';
      nameInput.id = 'report-nome';
      nameInput.maxLength = 160;
      nameInput.placeholder = 'Come si chiama adesso';
      form.append(field('Nuovo nome', nameInput));
    }

    if (kind === 'indirizzo') {
      addressInput.type = 'text';
      addressInput.id = 'report-indirizzo';
      addressInput.maxLength = 160;
      addressInput.placeholder = 'Via e numero civico';
      form.append(field('Indirizzo', addressInput));

      zoneInput.type = 'text';
      zoneInput.id = 'report-zona';
      zoneInput.maxLength = 160;
      zoneInput.placeholder = 'Facoltativo, se è cambiata';
      zoneInput.value = venue.zona ?? '';
      form.append(field('Zona', zoneInput));
    }

    // --- quando ---
    const when = el('input');
    when.type = 'date';
    when.id = 'report-quando';
    when.max = today();
    when.value = today();
    form.append(
      field(kind === 'stato' ? 'Quando ci sei stato' : 'Quando l’hai visto', when)
    );

    // --- nota facoltativa ---
    const note = el('textarea');
    note.id = 'report-nota';
    note.rows = 3;
    note.maxLength = 500;
    note.placeholder = 'Facoltativo: cosa hai visto, orari, cambio di gestione…';
    form.append(field('Nota', note));

    // Trappola per gli invii automatici: nessuna persona la vede, nessun
    // client vero la riempie.
    const honeypot = el('input');
    honeypot.type = 'text';
    honeypot.name = 'azienda';
    honeypot.tabIndex = -1;
    honeypot.autocomplete = 'off';
    honeypot.className = 'sr';
    honeypot.setAttribute('aria-hidden', 'true');
    form.append(honeypot);

    const send = el('button', 'btn btn--primary btn--wide', 'Invia la segnalazione');
    send.type = 'submit';
    form.append(send);

    const status = el('p', 'report__status');
    status.setAttribute('role', 'status');
    form.append(status);


    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const refuse = (message) => {
        status.className = 'report__status is-bad';
        status.textContent = message;
      };

      const payload = {
        tipo: kind,
        locale_id: venue.id,
        visitato_il: when.value || null,
        nota: note.value || null,
        azienda: honeypot.value,
      };

      if (kind === 'stato') {
        if (!chosen) return refuse('Scegli se era aperto o chiuso.');
        payload.stato = chosen;
      } else if (kind === 'nome') {
        const value = nameInput.value.trim();
        if (!value) return refuse('Scrivi il nome nuovo.');
        if (value === venue.nome) return refuse('È già il nome che abbiamo in catalogo.');
        payload.nome = value;
      } else {
        const value = addressInput.value.trim();
        if (!value) return refuse('Scrivi l’indirizzo.');
        payload.indirizzo = value;
        payload.zona = zoneInput.value.trim() || null;
      }

      send.disabled = true;
      status.className = 'report__status';
      status.textContent = 'Invio in corso…';

      try {
        const response = await fetch(ENDPOINT, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          form.replaceChildren(sent(when.value));
          return;
        }

        // The server's own words, whatever went wrong: a canned message chosen
        // by status code used to say "not active yet" for any outage. When the
        // answer carries no words (a platform error page), the status says it.
        const body = await response.json().catch(() => ({}));
        status.className = 'report__status is-bad';
        status.textContent =
          body.errore ?? `${MESSAGES.generico} (errore del server ${response.status})`;
      } catch {
        status.className = 'report__status is-bad';
        status.textContent = MESSAGES.rete;
      } finally {
        send.disabled = false;
      }
    });

    return form;
  }

  /** What replaces the form once it has gone through. */
  function sent(date) {
    const done = el('div', 'report__done');
    done.append(el('h2', 'report__name', 'Segnalazione inviata'));
    done.append(el('p', 'report__intro', MESSAGES.ok));
    // Costruita senza articolo davanti alla data: "al 8 settembre" non si può
    // leggere, e la data arriva dal visitatore, quindi non si sa mai che
    // giorno sarà.
    const detail = formatDate(date);
    const what =
      kind === 'stato'
        ? chosen === 'aperto'
          ? 'era aperto'
          : 'era chiuso'
        : kind === 'nome'
          ? 'ha cambiato nome'
          : 'si è spostato';
    done.append(
      el(
        'p',
        'note',
        detail ? `Hai segnalato «${what}» — visto il ${detail}.` : `Hai segnalato «${what}».`
      )
    );
    const close = el('button', 'btn btn--wide btn--spaced', 'Chiudi');
    close.type = 'button';
    close.addEventListener('click', onClose);
    done.append(close);
    return done;
  }

  return {
    open(nextVenue) {
      venue = nextVenue;
      chosen = null;
      kind = 'stato';
      container.replaceChildren(render());
      container.scrollTop = 0;
    },
  };
}
